import React from 'react';
import { motion } from 'framer-motion';
import { Plane, Compass, MapPin, Glasses, FileCheck, Building2, Shield, Ruler, CheckCircle2 } from 'lucide-react';

const Portfolio = () => {
  // Statistiche aggregate dei lavori
  const stats = [
    {
      id: 1,
      title: "Aerofotogrammetria",
      count: "50+",
      description: "Rilievi aerofotogrammetrici con droni professionali",
      icon: Plane,
      color: "from-blue-500/20 to-cyan-500/20",
      borderColor: "border-blue-500/30",
      iconColor: "text-blue-400",
      details: [
        "Parchi Nazionali",
        "Aree urbane e rurali",
        "Grandi estensioni fino a 400ha",
        "Rilievi multispettrali"
      ]
    },
    {
      id: 2,
      title: "Rilievi Topografici",
      count: "100+",
      description: "Rilievi di precisione con GPS RTK e stazione totale",
      icon: Compass,
      color: "from-emerald-500/20 to-green-500/20",
      borderColor: "border-emerald-500/30",
      iconColor: "text-emerald-400",
      details: [
        "Terreni agricoli",
        "Proprietà residenziali",
        "Aree boschive",
        "Zone aeronautiche"
      ]
    },
    {
      id: 3,
      title: "Picchettamenti",
      count: "80+",
      description: "Tracciamenti e posizionamento punti fissi per cantieri",
      icon: MapPin,
      color: "from-orange-500/20 to-amber-500/20",
      borderColor: "border-orange-500/30",
      iconColor: "text-orange-400",
      details: [
        "Infrastrutture stradali",
        "Ponti e viadotti",
        "Fondazioni edifici",
        "Piazzali e parcheggi"
      ]
    },
    {
      id: 4,
      title: "Riconfinamenti Catastali",
      count: "60+",
      description: "Determinazione confini e pratiche catastali",
      icon: Ruler,
      color: "from-purple-500/20 to-pink-500/20",
      borderColor: "border-purple-500/30",
      iconColor: "text-purple-400",
      details: [
        "Terreni agricoli",
        "Proprietà urbane",
        "Frazionamenti",
        "Verifiche confinarie"
      ]
    },
    {
      id: 5,
      title: "Visualizzazioni VR",
      count: "15+",
      description: "Progetti con visualizzazione immersiva in Virtual Reality",
      icon: Glasses,
      color: "from-[#D4AF37]/20 to-[#F4E5B8]/20",
      borderColor: "border-[#D4AF37]/30",
      iconColor: "text-[#D4AF37]",
      details: [
        "Interni residenziali",
        "Spazi commerciali",
        "Tour virtuali 360°",
        "Presentazioni clienti"
      ]
    },
    {
      id: 6,
      title: "Pratiche Urbanistiche",
      count: "70+",
      description: "Variazioni catastali e pratiche edilizie",
      icon: FileCheck,
      color: "from-indigo-500/20 to-violet-500/20",
      borderColor: "border-indigo-500/30",
      iconColor: "text-indigo-400",
      details: [
        "Variazioni DOCFA",
        "Aggiornamenti catastali",
        "Studi di fattibilità",
        "Pratiche comunali"
      ]
    },
    {
      id: 7,
      title: "Direzione Lavori",
      count: "40+",
      description: "Gestione cantieri e coordinamento sicurezza",
      icon: Shield,
      color: "from-red-500/20 to-rose-500/20",
      borderColor: "border-red-500/30",
      iconColor: "text-red-400",
      details: [
        "Coordinamento CSE/CSP",
        "Piani di Sicurezza",
        "Stati Avanzamento Lavori",
        "Contabilità cantiere"
      ]
    },
    {
      id: 8,
      title: "Modellazione BIM",
      count: "25+",
      description: "Restituzioni 3D e modelli Building Information Modeling",
      icon: Building2,
      color: "from-cyan-500/20 to-teal-500/20",
      borderColor: "border-cyan-500/30",
      iconColor: "text-cyan-400",
      details: [
        "Edifici residenziali",
        "Condomini",
        "Rendering fotorealistici",
        "Nuvole di punti"
      ]
    }
  ];

  const totalProjects = stats.reduce((acc, stat) => {
    const num = parseInt(stat.count.replace('+', ''));
    return acc + num;
  }, 0);

  return (
    <div className="bg-[#0A1628] min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-semibold mb-4"
          >
            Portfolio Lavori
          </motion.span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Esperienza <span className="text-[#D4AF37]">Comprovata</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed mb-8">
            Oltre 5 anni di attività professionale con centinaia di progetti completati con successo in tutta Italia.
          </p>
          
          {/* Total Counter */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="inline-flex items-center space-x-4 bg-gradient-to-r from-[#1A2B47] to-[#0A1628] backdrop-blur-xl border border-[#D4AF37]/30 rounded-2xl px-8 py-6 shadow-2xl"
          >
            <CheckCircle2 className="w-12 h-12 text-[#D4AF37]" />
            <div className="text-left">
              <p className="text-5xl font-bold text-[#D4AF37]">{totalProjects}+</p>
              <p className="text-gray-400 text-sm font-medium">Lavori Completati</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                {/* Card */}
                <div className={`relative h-full bg-gradient-to-br from-[#1A2B47]/80 to-[#0A1628]/80 backdrop-blur-xl border ${stat.borderColor} rounded-3xl p-6 group-hover:border-opacity-60 transition-all duration-500 shadow-xl`}>
                  {/* Icon */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                      <IconComponent className={`w-7 h-7 ${stat.iconColor}`} />
                    </div>
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.08 + 0.3 }}
                      className={`text-4xl font-bold ${stat.iconColor}`}
                    >
                      {stat.count}
                    </motion.span>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors duration-300">
                    {stat.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                    {stat.description}
                  </p>
                  
                  {/* Details List */}
                  <div className="space-y-2 pt-4 border-t border-white/5">
                    {stat.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <div className={`w-1.5 h-1.5 ${stat.iconColor} rounded-full mt-1.5 flex-shrink-0`} />
                        <span className="text-xs text-gray-500">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-3xl p-12 border border-[#D4AF37]/20 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.1),transparent_70%)]" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Hai un progetto in mente?
              </h2>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                Contattaci per una consulenza gratuita e scopri come possiamo aiutarti a realizzare il tuo progetto.
              </p>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(212,175,55,0.4)' }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A1628] rounded-full font-semibold text-lg shadow-lg transition-all duration-300"
              >
                <span>Richiedi Preventivo</span>
                <CheckCircle2 className="w-5 h-5" />
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Portfolio;
